// src/ui/Sidebar.jsx
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  BarChart3,
  FileText,
  Users,
  Settings,
  CalendarClock,
  FolderKanban,
  Database,
  ListTree,
  ChevronDown,
  ChevronRight,
  Mail,
  MessageSquare,
  Send,
  Facebook,
  MessageSquareCode,
  MessageCircle,
  Search,
  Tag,
} from "lucide-react";
import { useEffect, useState } from "react";
import { features } from "../config/features";

export default function Sidebar({ collapsed }) {
  const { isAuth, user } = useAuth();
  const location = useLocation();
  const isAdmin = isAuth && user?.role === "admin";

  const navSections = [
    {
      label: "Atención",
      groups: [
        {
          key: "clientes",
          label: "Clientes y leads",
          icon: Users,
          children: [
            { to: "/leads", label: "Leads", icon: Users },
            { to: "/contactos", label: "Contactos", icon: Users },
            { to: "/internal", label: "Bandeja interna", icon: MessageCircle },
          ],
        },
        {
          key: "citas",
          label: "Agenda médica",
          icon: CalendarClock,
          children: [
            { to: "/appointments", label: "Citas", icon: ListTree },
            { to: "/appointments/availability", label: "Disponibilidad", icon: CalendarClock },
            { to: "/appointments/services", label: "Servicios", icon: ListTree },
            { to: "/appointments/locations", label: "Sedes", icon: ListTree },
            { to: "/appointments/resources", label: "Médicos / recursos", icon: ListTree },
            { to: "/appointments/reasons", label: "Motivos", icon: ListTree },
            { to: "/appointments/schedule-rules", label: "Horarios", icon: ListTree },
            { to: "/appointments/schedule-blackouts", label: "Bloqueos", icon: ListTree },
          ],
        },
      ],
    },
    {
      label: "Canales",
      groups: [
        {
          key: "whatsapp",
          label: "WhatsApp",
          icon: MessageSquare,
          children: [
            { to: "/dashboard-api", label: "Pruebas Cloud API", icon: MessageSquare },
            { to: "/settings", label: "Conexiones", icon: Settings, hidden: !isAdmin },
          ],
        },
        {
          key: "telegram",
          label: "Telegram",
          icon: Send,
          children: [
            { to: "/dashboard-web/telegram", label: "Bots Telegram", icon: Send },
          ],
        },
        {
          key: "facebook",
          label: "Facebook",
          icon: Facebook,
          children: [
            {
              to: "/facebook/connections",
              label: "Conexiones",
              icon: ListTree,
              hidden: !isAdmin,
            },
            { to: "/facebook/pages", label: "Páginas", icon: ListTree, hidden: !isAdmin },
          ],
        },
        {
          key: "mail",
          label: "Correo",
          icon: Mail,
          children: [
            { to: "/mail/settings", label: "SMTP", icon: Settings, hidden: !isAdmin },
          ],
        },
      ],
    },
    {
      label: "Crecimiento",
      groups: [
        {
          key: "campanas",
          label: "Campañas",
          icon: FileText,
          children: [
            { to: "/whatsApp-web", label: "Envíos WhatsApp", icon: MessageSquare },
            { to: "/envio-mail", label: "Envíos email", icon: Mail },
            { to: "/templates", label: "Plantillas de envío", icon: FileText },
            { to: "/promociones", label: "Promociones", icon: Tag, hidden: !isAdmin },
            { to: "/reports/history", label: "Histórico de envíos", icon: ListTree, hidden: !isAdmin },
            {
              to: "/messages/templates",
              label: "Plantillas de mensajes",
              icon: MessageSquareCode,
              hidden: !isAdmin,
            },
            {
              to: "/messages/chanels",
              label: "Permisos de plantillas",
              icon: ListTree,
              hidden: true,
            },
          ],
        },
        {
          key: "automatizacion",
          label: "Automatización",
          icon: Settings,
          children: [
            { to: "/bots", label: "Bots y reglas", icon: Settings, hidden: !isAdmin },
            {
              to: "/flow-3",
              label: "Flow 3.0",
              icon: ListTree,
              hidden: !isAdmin || !features.flow3,
            },
          ],
        },
      ],
    },
    {
      label: "Gestión",
      groups: [
        {
          key: "seguimiento",
          label: "Seguimiento",
          icon: Search,
          children: [
            { to: "/bitacoras", label: "Bitácoras", icon: Search },
            { to: "/siniestralidad", label: "Siniestralidad", icon: Search },
          ],
        },
        {
          key: "reportes",
          label: "Reportes",
          icon: BarChart3,
          children: [
            { to: "/reports", label: "Dashboard", icon: BarChart3, hidden: !isAdmin },
          ],
        },
        {
          key: "sistema",
          label: "Sistema",
          icon: FolderKanban,
          children: [
            { to: "/admin/users", label: "Usuarios", icon: Users, hidden: !isAdmin },
            { to: "/logs", label: "Logs", icon: ListTree, hidden: !isAdmin },
            { to: "/admin/sqlite", label: "SQLite", icon: Database, hidden: !isAdmin },
          ],
        },
      ],
    },
  ];

  // Estado de grupos abiertos
  const [openGroups, setOpenGroups] = useState({});

  // Al cambiar de ruta, abrir el grupo que contenga la ruta activa
  useEffect(() => {
    const path = location.pathname;
    const next = { ...openGroups };

    navSections.flatMap((section) => section.groups).forEach((group) => {
      const hasActive = group.children
        .filter((c) => !c.hidden)
        .some((c) => path.toLowerCase().startsWith(c.to.toLowerCase()));
      if (hasActive) next[group.key] = true;
    });

    setOpenGroups((prev) => ({ ...prev, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (key) => {
    if (collapsed) return;
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderGroup = (group, size = "main") => {
    const visibleChildren = group.children.filter((c) => !c.hidden);
    if (!visibleChildren.length) return null;

    const GroupIcon = group.icon;
    const isOpen = !!openGroups[group.key];

    const headerTextClass = size === "main" ? "text-sm" : "text-xs";
    const iconClass = size === "main" ? "h-5 w-5" : "h-4 w-4";

    return (
      <div key={group.key} className="mb-1">
        <button
          type="button"
          onClick={() => toggleGroup(group.key)}
          className={[
            "w-full flex items-center gap-3 px-3 py-2 text-neutral-400",
            headerTextClass,
            "hover:bg-neutral-900 rounded-xl",
            collapsed ? "justify-center" : "",
          ].join(" ")}
        >
          <GroupIcon className={`${iconClass} flex-shrink-0`} />
          {!collapsed && (
            <>
              <span className="font-semibold flex-1 text-left">{group.label}</span>
              {isOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </>
          )}
        </button>

        {!collapsed && isOpen && (
          <div className="mt-1 space-y-0.5">
            {visibleChildren.map(({ to, label, icon: ChildIcon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs ml-6",
                    isActive
                      ? "bg-neutral-800 text-white"
                      : "text-neutral-300 hover:bg-neutral-900 hover:text-white",
                  ].join(" ")
                }
              >
                {ChildIcon ? (
                  <ChildIcon className="h-3 w-3 flex-shrink-0" />
                ) : (
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 flex-shrink-0" />
                )}
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={
        "border-r border-neutral-800 bg-neutral-950 transition-all duration-200 h-full overflow-y-auto " +
        (collapsed ? "w-16" : "w-64")
      }
    >
      <div className="mx-auto max-w-[240px] px-2 py-3">
        <nav className="space-y-1">
          {navSections.map((section) => {
            const visibleGroups = section.groups.filter((group) =>
              group.children.some((child) => !child.hidden)
            );
            if (!visibleGroups.length) return null;

            return (
              <div key={section.label}>
                {!collapsed && (
                  <div className="mt-4 mb-1 px-3 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                    {section.label}
                  </div>
                )}
                {visibleGroups.map((group) => renderGroup(group, "main"))}
              </div>
            );
          })}
        </nav>

        {/* Footer versión */}
        {!collapsed && (
          <div className="mt-4 rounded-xl border border-neutral-800 p-3 text-[11px] text-neutral-400">
            WhatsPlatinum {features.flow3 ? "v3.0 Flow" : "v2.0"}
          </div>
        )}
      </div>
    </aside>
  );
}
